import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { downloadRecording } from '../../utils/recordingApi';

const DownloadManager = ({ recordingId, filename, onDownloadStart, onDownloadComplete, onDownloadError }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadSpeed, setDownloadSpeed] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(0);
  const abortControllerRef = useRef(null);

  const formatTime = (seconds) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const formatSpeed = (bytesPerSecond) => {
    if (bytesPerSecond < 1024) return `${bytesPerSecond} B/s`;
    if (bytesPerSecond < 1024 * 1024) return `${(bytesPerSecond / 1024).toFixed(1)} KB/s`;
    if (bytesPerSecond < 1024 * 1024 * 1024) return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`;
    return `${(bytesPerSecond / (1024 * 1024 * 1024)).toFixed(1)} GB/s`;
  };

  const handleDownload = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    setDownloadProgress(0);
    setDownloadSpeed(0);
    setEstimatedTime(0);
    
    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();
    
    const startTime = Date.now();
    let lastProgressTime = startTime;
    let lastProgressBytes = 0;

    try {
      onDownloadStart && onDownloadStart();

      // Create a custom download function with progress tracking
      const response = await fetch(`/recording/${recordingId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const contentLength = response.headers.get('content-length');
      const totalBytes = contentLength ? parseInt(contentLength, 10) : 0;
      
      if (!response.body) {
        throw new Error('No response body received');
      }

      const reader = response.body.getReader();
      const chunks = [];
      let receivedBytes = 0;

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        receivedBytes += value.length;
        
        // Update progress
        const progress = totalBytes > 0 ? (receivedBytes / totalBytes) * 100 : 0;
        setDownloadProgress(progress);
        
        // Calculate speed and ETA
        const currentTime = Date.now();
        const timeElapsed = (currentTime - lastProgressTime) / 1000;
        
        if (timeElapsed >= 1) { // Update every second
          const bytesDownloaded = receivedBytes - lastProgressBytes;
          const speed = bytesDownloaded / timeElapsed;
          setDownloadSpeed(speed);
          
          if (speed > 0 && totalBytes > 0) {
            const remainingBytes = totalBytes - receivedBytes;
            const eta = remainingBytes / speed;
            setEstimatedTime(eta);
          }
          
          lastProgressTime = currentTime;
          lastProgressBytes = receivedBytes;
        }
      }

      // Create blob and download
      const blob = new Blob(chunks, { type: 'video/mp4' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `recording-${recordingId}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      onDownloadComplete && onDownloadComplete();
      toast.success('Download completed successfully!');

    } catch (error) {
      if (error.name === 'AbortError') {
        toast.info('Download cancelled');
      } else {
        console.error('Download error:', error);
        toast.error(error.message || 'Download failed');
        onDownloadError && onDownloadError(error);
      }
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
      setDownloadSpeed(0);
      setEstimatedTime(0);
      abortControllerRef.current = null;
    }
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  return (
    <div className="space-y-3">
      {!isDownloading ? (
        <button
          onClick={handleDownload}
          className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
        >
          <span>⬇️</span>
          Download Recording
        </button>
      ) : (
        <div className="space-y-3">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Downloading...</span>
              <span>{Math.round(downloadProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${downloadProgress}%` }}
              ></div>
            </div>
          </div>

          {/* Download Stats */}
          <div className="flex justify-between text-xs text-gray-600">
            <span>Speed: {formatSpeed(downloadSpeed)}</span>
            {estimatedTime > 0 && (
              <span>ETA: {formatTime(estimatedTime)}</span>
            )}
          </div>

          {/* Cancel Button */}
          <button
            onClick={handleCancel}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Cancel Download
          </button>
        </div>
      )}
    </div>
  );
};

export default DownloadManager;
